import React, { Suspense, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { startCheckoutRescue } from 'src/redux/actions/assistant';
import { closeChatCheckout, openAssistant } from 'src/redux/slices/assistant';

// Only for a checkout a chat card opens: kept out of the root bundle.
const BuyCreditsDialog = React.lazy(
  () => import('src/components/buyCreditsDialog'),
);

/** The reason the chat's own Pro checkout reports, so leaving it goes back to the chat. */
export const CHAT_CHECKOUT_REASON = 'assistant_rescue';

// Lets the checkout finish closing before the chat slides in over the page.
const RESCUE_OPEN_DELAY_MS = 600;
// A sheet still up when a checkout is left -- the pitch an embedded checkout
// goes back to, a course paywall -- would hide the chat under it: wait for it
// to close, then open. Long enough to read a pitch; past that, let it go.
const RESCUE_WAIT_POLL_MS = 1000;
const RESCUE_WAIT_MAX_MS = 10 * 60 * 1000;
const isModalOpen = () =>
  !!document.querySelector('.MuiModal-root:not(.MuiModal-hidden)');

/**
 * A Pro checkout left unfinished (checkoutAbandoned): back to the chat when a
 * chat card opened it, else ask the server for a follow-up chat, which opens
 * the panel when one is due (startCheckoutRescue).
 */
const useCheckoutRescue = (active: boolean) => {
  const dispatch = useAppDispatch();
  const { abandonedCheckout, isOpen } = useAppSelector(
    (state) => state.assistant,
  );
  const latest = useRef({ active, isOpen });
  useEffect(() => {
    latest.current = { active, isOpen };
  }, [active, isOpen]);

  useEffect(() => {
    if (!abandonedCheckout) return undefined;
    let id: number;
    const attempt = () => {
      const now = latest.current;
      if (!now.active || now.isOpen) return;
      if (isModalOpen()) {
        if (Date.now() - abandonedCheckout.at < RESCUE_WAIT_MAX_MS) {
          id = window.setTimeout(attempt, RESCUE_WAIT_POLL_MS);
        }
        return;
      }
      if (abandonedCheckout.reason === CHAT_CHECKOUT_REASON) {
        dispatch(openAssistant());
      } else {
        dispatch(
          startCheckoutRescue({
            reason: abandonedCheckout.reason,
            path: abandonedCheckout.path,
          }),
        );
      }
    };
    id = window.setTimeout(attempt, RESCUE_OPEN_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [abandonedCheckout, dispatch]);
};

interface CheckoutRescueProps {
  /** The assistant can show here: not on a focus screen. */
  active: boolean;
}

/**
 * The applicant's app-opened chat: the follow-up after a Pro checkout is
 * left, and the checkout its Go Pro card opens.
 */
const CheckoutRescue = ({ active }: CheckoutRescueProps) => {
  const dispatch = useAppDispatch();
  const chatCheckoutItemId = useAppSelector(
    (state) => state.assistant.chatCheckoutItemId,
  );
  useCheckoutRescue(active);

  // Not over a focus screen, where the assistant is away.
  if (!chatCheckoutItemId || !active) return null;
  return (
    <Suspense fallback={null}>
      <BuyCreditsDialog
        open
        directPurchase={{ itemId: chatCheckoutItemId }}
        trigger="browse"
        reason={CHAT_CHECKOUT_REASON}
        onClose={() => dispatch(closeChatCheckout())}
      />
    </Suspense>
  );
};

export default CheckoutRescue;
